import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { PlayerCtx, type DateRange, type PlaybackDock, type PlayerContextValue } from './playerCtx';
import {
  normalizeRangeStart,
  getMinuteCount,
  getTimestampAtMinute,
  clampMinuteOffset,
  getMinuteOffsetForTimestamp,
  getClosestMinuteOffsetToNow,
} from './generateMinuteClock';

export const PlayerProvider = ({ children }: { children: ReactNode }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [autoLoop, setAutoLoop] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(null);
  const [liveStartMinutes, setLiveStartMinutes] = useState(-5);
  const [liveEndMinutes, setLiveEndMinutes] = useState(60);
  const [liveEndMinutesForRadar, setLiveEndMinutesForRadar] = useState(30);
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [playbackDock, setPlaybackDock] = useState<PlaybackDock>(
    () => (localStorage.getItem('nowcasting-playback-dock') as PlaybackDock) || 'now',
  );

  const animationRef = useRef<number | null>(null);
  const currentIndexRef = useRef(0);
  const clockMinuteCountRef = useRef(0);
  const autoLoopRef = useRef(false);
  const prevTimestampRef = useRef<Date | null>(null);

  const clockStart = useMemo(
    () => (selectedDateRange ? normalizeRangeStart(selectedDateRange.start) : null),
    [selectedDateRange],
  );

  const clockMinuteCount = useMemo(() => {
    if (!selectedDateRange || !clockStart) return 0;
    return getMinuteCount(clockStart, selectedDateRange.end);
  }, [selectedDateRange, clockStart]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    clockMinuteCountRef.current = clockMinuteCount;
  }, [clockMinuteCount]);

  useEffect(() => {
    autoLoopRef.current = autoLoop;
  }, [autoLoop]);

  const playbackDockRef = useRef(playbackDock);
  const isLiveModeRef = useRef(isLiveMode);
  useEffect(() => { playbackDockRef.current = playbackDock; }, [playbackDock]);
  useEffect(() => { isLiveModeRef.current = isLiveMode; }, [isLiveMode]);

  const handleSetPlaybackDock = useCallback((dock: PlaybackDock) => {
    setPlaybackDock(dock);
    localStorage.setItem('nowcasting-playback-dock', dock);
  }, []);

  // Reconcile minute offset when range changes
  useEffect(() => {
    if (!clockStart || clockMinuteCount === 0) {
      currentIndexRef.current = 0;
      setCurrentIndex(0);
      prevTimestampRef.current = null;
      return;
    }

    let targetIdx: number;

    if (isLiveModeRef.current) {
      const dock = playbackDockRef.current;
      switch (dock) {
        case 'first':
          targetIdx = 0;
          break;
        case 'last':
          targetIdx = clockMinuteCount - 1;
          break;
        case 'now':
          targetIdx = getClosestMinuteOffsetToNow(clockStart, clockMinuteCount);
          break;
        case 'current':
        default: {
          const prevTs = prevTimestampRef.current;
          if (prevTs) {
            targetIdx = getMinuteOffsetForTimestamp(clockStart, prevTs, clockMinuteCount);
          } else {
            targetIdx = clampMinuteOffset(currentIndexRef.current, clockMinuteCount);
          }
          break;
        }
      }
    } else {
      const prevTs = prevTimestampRef.current;
      if (prevTs) {
        targetIdx = getMinuteOffsetForTimestamp(clockStart, prevTs, clockMinuteCount);
      } else {
        targetIdx = clampMinuteOffset(currentIndexRef.current, clockMinuteCount);
      }
    }

    currentIndexRef.current = targetIdx;
    setCurrentIndex(targetIdx);
  }, [clockStart, clockMinuteCount, selectedDateRange]);

  const currentTimestamp = useMemo(() => {
    if (!clockStart || clockMinuteCount === 0) return null;
    return getTimestampAtMinute(clockStart, currentIndex);
  }, [clockStart, clockMinuteCount, currentIndex]);

  useEffect(() => {
    if (currentTimestamp) {
      prevTimestampRef.current = currentTimestamp;
    }
  }, [currentTimestamp]);

  // Animation loop
  useEffect(() => {
    if (!isPlaying) {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    if (clockMinuteCountRef.current === 0) return;

    const frameDelay = 1000 / playbackSpeed;

    animationRef.current = window.setInterval(() => {
      const idx = currentIndexRef.current;
      const len = clockMinuteCountRef.current;

      if (len === 0) {
        setIsPlaying(false);
        return;
      }

      if (idx >= len - 1) {
        if (autoLoopRef.current) {
          setCurrentIndex(0);
          currentIndexRef.current = 0;
        } else {
          setIsPlaying(false);
          if (animationRef.current) {
            clearInterval(animationRef.current);
            animationRef.current = null;
          }
        }
        return;
      }

      const next = idx + 1;
      currentIndexRef.current = next;
      setCurrentIndex(next);
    }, frameDelay);

    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isPlaying, playbackSpeed]);

  const play = useCallback(() => {
    if (clockMinuteCount === 0) return;
    if (currentIndex >= clockMinuteCount - 1) {
      setCurrentIndex(0);
      currentIndexRef.current = 0;
    }
    setIsPlaying(true);
  }, [clockMinuteCount, currentIndex]);

  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const stepForward = useCallback(() => {
    setCurrentIndex((prev) => {
      const next = Math.min(prev + 1, clockMinuteCountRef.current - 1);
      currentIndexRef.current = next;
      return next;
    });
  }, []);

  const stepBackward = useCallback(() => {
    setCurrentIndex((prev) => {
      const next = Math.max(prev - 1, 0);
      currentIndexRef.current = next;
      return next;
    });
  }, []);

  const handleSetCurrentIndex = useCallback((index: number) => {
    const clamped = clampMinuteOffset(index, clockMinuteCountRef.current);
    currentIndexRef.current = clamped;
    setCurrentIndex(clamped);
  }, []);

  const value = useMemo<PlayerContextValue>(
    () => ({
      currentTimestamp,
      isPlaying,
      playbackSpeed,
      clockStart,
      clockMinuteCount,
      currentIndex,
      autoLoop,
      selectedDateRange,
      liveStartMinutes,
      liveEndMinutes,
      liveEndMinutesForRadar,
      isLiveMode,
      playbackDock,
      play,
      pause,
      togglePlayPause,
      setCurrentIndex: handleSetCurrentIndex,
      setPlaybackSpeed,
      setAutoLoop,
      stepForward,
      stepBackward,
      setSelectedDateRange,
      setLiveStartMinutes,
      setLiveEndMinutes,
      setLiveEndMinutesForRadar,
      setIsLiveMode,
      setPlaybackDock: handleSetPlaybackDock,
    }),
    [
      currentTimestamp,
      isPlaying,
      playbackSpeed,
      clockStart,
      clockMinuteCount,
      currentIndex,
      autoLoop,
      selectedDateRange,
      liveStartMinutes,
      liveEndMinutes,
      liveEndMinutesForRadar,
      isLiveMode,
      playbackDock,
      play,
      pause,
      togglePlayPause,
      handleSetCurrentIndex,
      handleSetPlaybackDock,
    ],
  );

  return <PlayerCtx.Provider value={value}>{children}</PlayerCtx.Provider>;
};
